import React, { useEffect, useState } from "react";
import ttAPI from "../../api/ttAPI";
import {
  CircularProgress,
  Paper,
  Typography,
  Chip,
  Tooltip,
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  HelpOutline as HelpOutlineIcon,
  VpnKey as VpnKeyIcon,
  TrackChanges as TrackChangesIcon
} from "@material-ui/icons";

const getStatusConfig = (status) => {
  switch (status) {
    case "LOOKS_ALIGNED":
      return {
        label: "Looks aligned",
        color: "#2e7d32",
        background: "#e8f5e9",
        icon: <CheckCircleIcon style={{ fontSize: 18 }} />,
      };
    case "POSSIBLE_MISMATCH":
      return {
        label: "Check month",
        color: "#ef6c00",
        background: "#fff3e0",
        icon: <WarningIcon style={{ fontSize: 18 }} />,
      };
    case "CHECK_HISTORY":
      return {
        label: "Check history",
        color: "#0277bd",
        background: "#e1f5fe",
        icon: <InfoIcon style={{ fontSize: 18 }} />,
      };
    default:
      return {
        label: "Unknown",
        color: "#616161",
        background: "#f5f5f5",
        icon: <HelpOutlineIcon style={{ fontSize: 18 }} />,
      };
  }
};

const VisitPatternPanel = ({ retestId, isKeyCustomer, isKpiCustomer }) => {
  const [loading, setLoading] = useState(false);
  const [pattern, setPattern] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchPattern = async () => {
      if (!retestId || retestId <= 0) return;

      setLoading(true);

      try {
        const response = await ttAPI.getRetestVisitPatternSummary(retestId);
        if (mounted) {
          setPattern(response);
        }
      } catch (e) {
        console.error("Failed to load retest visit pattern summary", e);
        if (mounted) {
          setPattern(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPattern();

    return () => {
      mounted = false;
    };
  }, [retestId]);

  if (!retestId || retestId <= 0) {
    return null;
  }

  if (loading) {
    return (
      <Paper style={{ padding: 16, marginTop: 16 }}>
        <CircularProgress size={22} />
      </Paper>
    );
  }

  if (!pattern) {
    return null;
  }

  const status = getStatusConfig(pattern.alignment_status);

  return (
    <Paper
      elevation={1}
      style={{
        padding: "14px 18px",
        marginTop: 16,
        borderLeft: `5px solid ${status.color}`,
        background: "#fff",
      }}
    >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Visit Pattern
        </Typography>

        {isKeyCustomer && (
          <Tooltip title="Key customer">
            <VpnKeyIcon style={{ fontSize: 18, color: '#d4a017' }} />
          </Tooltip>
        )}

        {isKpiCustomer && (
          <Tooltip title="KPI customer">
            <TrackChangesIcon style={{ fontSize: 18, color: '#d4a017' }} />
          </Tooltip>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(100px, 1fr))",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div>
          <Typography variant="caption" color="textSecondary">
            Pattern
          </Typography>
          <Typography variant="body2">
            {pattern.recommended_pattern || "unknown"}
          </Typography>
        </div>

        <div>
          <Typography variant="caption" color="textSecondary">
            Last inspected
          </Typography>
          <Typography variant="body2">
            {pattern.last_inspected_month || "unknown"}
          </Typography>
        </div>

        <div>
          <Typography variant="caption" color="textSecondary">
            Expected
          </Typography>
          <Typography variant="body2">
            {pattern.expected_month || "unknown"}
          </Typography>
        </div>

        <div>
          <Typography variant="caption" color="textSecondary">
            Window
          </Typography>
          <Tooltip
            title={
              isKpiCustomer || isKeyCustomer
                ? "KPI/Key customer: booking months may need to follow specific requirements. Please check histories and requirements before confirming."
                : "Acceptable booking window based on pattern"
            }
          >
          <Typography
              variant="body2"
              style={{
                fontWeight: isKpiCustomer || isKeyCustomer ? 600 : 400,
                color: isKpiCustomer || isKeyCustomer ? "#d32f2f" : undefined, // warning red
              }}
              >
              {pattern.acceptable_from_month && pattern.acceptable_to_month
                ? `${pattern.acceptable_from_month} → ${pattern.acceptable_to_month}`
                : "unknown"}
              {(isKpiCustomer || isKeyCustomer) && " ⚠️"}
           </Typography>
          </Tooltip>
        </div>

        <div>
          <Typography variant="caption" color="textSecondary">
            Confidence
          </Typography>
          <Typography
              variant="body2"
              style={{
                fontWeight: 500,
                color:
                  pattern.confidence_label === "High"
                    ? "#2e7d32"
                    : pattern.confidence_label === "Medium"
                    ? "#ef6c00"
                    : "#616161",
              }}
            >
            {pattern.confidence_label || "Unknown"}
            {pattern.confidence_score !== undefined && pattern.confidence_score !== null
              ? ` (${pattern.confidence_score}%)`
              : ""}
          </Typography>
        </div>
      </div>

      <Typography variant="body2" color="textSecondary">
        {pattern.summary_text}
      </Typography>

      {pattern.warning_summary && (
          <Typography
            variant="body2"
            style={{ marginTop: 8, color: "#ef6c00", fontWeight: 500 }}
          >
            {pattern.warning_summary}
          </Typography>
        )}
    </Paper>
  );
};

export default VisitPatternPanel;
